import React, { useState, useEffect, useRef } from "react";
import { BellRing, Check, X, ChevronDown, Mail, CalendarDays } from "lucide-react";
import { memoIcona, memoLabel, isoGiorno } from "./MemoGiorno";
import * as api from "../api";

// Memo di oggi (e non fatti dei giorni scorsi): una riga compatta sopra la lista; tocco → tendina con l'elenco.
// Ogni memo si apre per leggere il testo completo e, se nato da un'email (Ricordamelo), riapre quell'email.
export default function MemoBanner({ memo, onChange, onApri, onOpenEmail }) {
  const oggi = isoGiorno(new Date());
  const attivi = (memo || []).filter((m) => !m.fatto && m.data <= oggi);
  const [aperto, setAperto] = useState(false);
  const [espanso, setEspanso] = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    if (!aperto) return;
    const chiudi = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setAperto(false);
    };
    document.addEventListener("mousedown", chiudi);
    document.addEventListener("touchstart", chiudi);
    return () => {
      document.removeEventListener("mousedown", chiudi);
      document.removeEventListener("touchstart", chiudi);
    };
  }, [aperto]);
  if (attivi.length === 0) return null;
  const titolo = attivi.length === 1 ? "1 memo per oggi" : `${attivi.length} memo per oggi`;
  return (
    <div className={`memo-banner ${aperto ? "aperto" : ""}`} ref={ref} data-testid="memo-banner">
      <button type="button" className="memo-banner-title" onClick={() => setAperto((v) => !v)} aria-expanded={aperto} data-testid="memo-banner-toggle">
        <BellRing size={16} strokeWidth={2.5} />
        <span className="memo-banner-titolo">{titolo}</span>
        {!aperto && <span className="memo-banner-anteprima">{attivi[0].testo}</span>}
        <ChevronDown size={16} strokeWidth={2.5} className="memo-banner-chevron" />
      </button>
      {aperto && (
        <div className="memo-banner-lista" data-testid="memo-banner-lista">
          {attivi.map((m) => (
            <MemoRiga
              key={m.id}
              m={m}
              oggi={oggi}
              espanso={espanso === m.id}
              onToggle={() => setEspanso(espanso === m.id ? null : m.id)}
              onFatto={() => api.setMemoFatto(m.id, true).then(onChange)}
              onElimina={() => api.deleteMemo(m.id).then(onChange)}
              onApriEmail={m.email_id && onOpenEmail ? () => { setAperto(false); onOpenEmail(m.email_id); } : null}
            />
          ))}
          <button type="button" className="memo-banner-calendario" onClick={() => { setAperto(false); onApri?.(); }} data-testid="memo-banner-calendario">
            <CalendarDays size={14} strokeWidth={2.4} /> Apri il calendario
          </button>
        </div>
      )}
    </div>
  );
}

function MemoRiga({ m, oggi, espanso, onToggle, onFatto, onElimina, onApriEmail }) {
  const Icon = memoIcona(m.tipo);
  const quando = `${memoLabel(m.tipo)}${m.ora ? ` · ${m.ora}` : ""}${m.data < oggi ? ` · dal ${m.data.split("-").reverse().join(".")}` : ""}`;
  return (
    <div className={`memo-banner-item ${espanso ? "aperto" : ""}`} data-testid={`memo-banner-${m.id}`}>
      <div className="memo-banner-riga" onClick={onToggle} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onToggle()} title={espanso ? "Riduci" : "Leggi tutto"}>
        <Icon size={15} strokeWidth={2.4} />
        <span className="memo-banner-text">
          <b>{quando}</b> {m.testo}
          {m.account_label ? <em> · {m.account_label}</em> : null}
        </span>
        <button className="icon-plain" title="Fatto" onClick={(e) => { e.stopPropagation(); onFatto(); }} data-testid={`memo-banner-fatto-${m.id}`}><Check size={16} /></button>
        <button className="icon-plain" title="Elimina" onClick={(e) => { e.stopPropagation(); onElimina(); }} data-testid={`memo-banner-del-${m.id}`}><X size={16} /></button>
      </div>
      {espanso && onApriEmail && (
        <button type="button" className="memo-banner-apri-email" onClick={onApriEmail} data-testid={`memo-banner-email-${m.id}`}>
          <Mail size={14} strokeWidth={2.4} /> Apri l'email{m.email_oggetto ? `: ${m.email_oggetto}` : ""}
        </button>
      )}
    </div>
  );
}
